export const POST_CONSULT_STATUSES = [
  "Sleep Test Scheduled", "Reserve Sleep Set", "Transferred To Center", "Assigned",
  "Technician Scored", "Assigned to Doctor", "Doctor Scored", "Post-Consult",
];

const DAY = 86400000;
const text = (value) => String(value || "").trim();
export const postConsultAssignee = (value) => {
  const name = text(value);
  return !name || name === "กรุณาเลือก" ? "Not assigned" : name;
};

export const postConsultDate = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? value : "";
  }
  const date = value && typeof value.toDate === "function" ? value.toDate() : new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return new Date(date.getTime() + 7 * 3600000).toISOString().slice(0, 10);
};

// The parent supplies only nodes permitted by the account's existing node access.
export const getPostConsultSummaryNodes = (currentUser, allowedNodes = []) => {
  if (!["admin", "super_admin"].includes((currentUser.roles || [])[0])) return [];
  return allowedNodes.some((node) => text(node.nodecode).toUpperCase() === "LPH") ? allowedNodes : [];
};

const timestamp = (value) => {
  if (!value) return 0;
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isFinite(date.getTime()) ? date.getTime() : 0;
};

const countStatuses = (cases) => {
  const groups = new Map();
  cases.forEach((row) => {
    const status = row.status || "Not specified";
    groups.set(status, (groups.get(status) || 0) + 1);
  });
  const priority = ["Doctor Scored", "Post-Consult", ...POST_CONSULT_STATUSES];
  const rank = (status) => priority.includes(status) ? priority.indexOf(status) : priority.length;
  return Array.from(groups, ([status, count]) => ({ status, count }))
    .sort((a, b) => rank(a.status) - rank(b.status) || a.status.localeCompare(b.status));
};

export const summarizePostConsult = (bookings, { startDate, endDate, mode = "short", customer = "", nodeLabel = "All accessible nodes", now = new Date() }) => {
  const asOf = postConsultDate(now);
  if (!postConsultDate(startDate) || !postConsultDate(endDate) || startDate > endDate) {
    throw new Error("Select a valid latest sleep study date range.");
  }
  // One sleep study order is one case. Missing orders retain their own document identity.
  const unique = new Map();
  bookings.forEach((booking, index) => {
    const props = booking.extendedProps || {};
    const key = text(props.order) || booking.sourcePath || text(props.uuid) || booking.id || `row-${index}`;
    const previous = unique.get(key);
    if (!previous || timestamp(props.updatedAt || booking.start) >
        timestamp((previous.extendedProps || {}).updatedAt || previous.start)) unique.set(key, booking);
  });
  const assignmentCases = Array.from(unique.values()).map((booking) => {
    const props = booking.extendedProps || {};
    const studyDate = postConsultDate(props.latestSleepStudyDate);
    const daysSinceStudy = studyDate ? Math.round((Date.parse(asOf) - Date.parse(studyDate)) / DAY) : null;
    return {
      key: booking.sourcePath || props.uuid || booking.id || props.order,
      order: text(props.order), hn: text(props.hn),
      patient: `${text(props.firstname)} ${text(props.lastname)}`.trim(),
      node: text(props.node), customer: text(props.customer), studyDate, status: text(props.status),
      tech: postConsultAssignee(props.techConsult), doctor: postConsultAssignee(props.postDoctor),
      daysSinceStudy, overdueDays: daysSinceStudy === null ? null : Math.max(0, daysSinceStudy - 45),
    };
  }).filter((row) => row.hn && row.studyDate >= startDate && row.studyDate <= endDate &&
    !["Cancel", "Failure"].includes(row.status) && (!customer || row.customer === customer));
  const cases = assignmentCases;
  const counts = (field) => {
    const groups = new Map();
    assignmentCases.forEach((row) => {
      if (!groups.has(row[field])) groups.set(row[field], []);
      groups.get(row[field]).push(row);
    });
    return Array.from(groups, ([name, rows]) => ({ name, count: rows.length, statuses: countStatuses(rows) }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "th"));
  };
  const overdue = cases.filter((row) => row.daysSinceStudy > 45 && POST_CONSULT_STATUSES.includes(row.status))
    .sort((a, b) => b.daysSinceStudy - a.daysSinceStudy || a.order.localeCompare(b.order));
  return { startDate, endDate, mode, asOf, nodeLabel, customer, total: cases.length, assignmentTotal: assignmentCases.length, assignmentCases, statuses: countStatuses(cases), techs: counts("tech"), doctors: counts("doctor"), cases, overdue };
};

export const postConsultExportSheets = (summary) => {
  const caseRows = (rows) => [
    ["Order", "HN", "Patient", "Node", "Customer", "Latest Sleep Study Date", "Current Status", "Post Consult Tech", "Post Consult Doctor", "Days Since Study", "Days Overdue (>45)"],
    ...rows.map((row) => [row.order, row.hn, row.patient, row.node, row.customer, row.studyDate, row.status, row.tech, row.doctor, row.daysSinceStudy, row.overdueDays]),
  ];
  return [
    { name: "Post Consult Summary", rows: [
      ["Post Consult Summary"], ["Latest Sleep Study Date From", summary.startDate],
      ["Latest Sleep Study Date To", summary.endDate], ["Overdue As Of (Bangkok)", summary.asOf],
      ["Node", summary.nodeLabel], ["Customer", summary.customer || "All Customers"],
      ["Summary Mode", summary.mode === "full" ? "Full" : "Short"],
      ["Assignment Counts (both modes)", "All statuses except Cancel and Failure"],
      ["Case List / Status Totals", "All statuses except Cancel and Failure"],
      ["HN Requirement", "Cases without HN are excluded"],
      ["Overdue Statuses", POST_CONSULT_STATUSES.join(", ")],
      ["Total Cases", summary.total], ["Overdue Cases (>45 days)", summary.overdue.length], [],
      ["Assignment Cases (all eligible statuses)", summary.assignmentTotal], [],
      ...(summary.mode === "full" ? [["Current Status", "Cases"], ...summary.statuses.map(({ status, count }) => [status, count]), []] : []),
      ["Post Consult Tech", "Cases"], ...summary.techs.map(({ name, count }) => [name, count]), [],
      ["Post Consult Doctor", "Cases"], ...summary.doctors.map(({ name, count }) => [name, count]),
      ...(summary.mode === "full" ? [[], ["Post Consult Tech", "Current Status", "Cases"],
      ...summary.techs.reduce((rows, person) => rows.concat(person.statuses.map(({ status, count }) => [person.name, status, count])), []),
      [], ["Post Consult Doctor", "Current Status", "Cases"],
      ...summary.doctors.reduce((rows, person) => rows.concat(person.statuses.map(({ status, count }) => [person.name, status, count])), []),
      ] : []),
    ] },
    { name: "Overdue Cases", rows: caseRows(summary.overdue) },
    { name: "Selected Cases", rows: caseRows(summary.cases) },
    { name: "Assignment Cases", rows: caseRows(summary.assignmentCases) },
  ];
};
